import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Target,
  Heart,
  Lightbulb,
  Users,
  Shield,
  Code2,
} from 'lucide-react';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                Building the Future of{' '}
                <span className="text-primary">Developer Productivity</span>
              </h1>
              <p className="mt-6 text-xl text-muted-foreground">
                We're on a mission to help engineering teams ship better software,
                faster. ForgeNetwork transforms how developers understand, document,
                and evolve their code.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-24 items-center">
              <div>
                <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                  Our Mission
                </span>
                <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                  Democratizing Code Intelligence
                </h2>
                <p className="mt-6 text-lg text-muted-foreground">
                  Every engineering team deserves access to the tools that make
                  understanding and documenting code effortless. We believe AI should
                  augment human developers, not replace them—turning complexity into
                  clarity.
                </p>
                <p className="mt-4 text-lg text-muted-foreground">
                  ForgeNetwork was born from the frustration of spending more time
                  understanding code than writing it. We've experienced the pain of
                  stale documentation, knowledge silos, and the endless cycle of
                  explaining the same architecture to new team members.
                </p>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                {[
                  {
                    icon: Target,
                    title: 'Focus',
                    description: 'We solve real problems that slow down engineering teams.',
                  },
                  {
                    icon: Heart,
                    title: 'Empathy',
                    description: 'We build tools we want to use ourselves.',
                  },
                  {
                    icon: Lightbulb,
                    title: 'Innovation',
                    description: 'We push the boundaries of what AI can do for developers.',
                  },
                  {
                    icon: Users,
                    title: 'Community',
                    description: 'We grow together with our users.',
                  },
                ].map((value) => (
                  <div
                    key={value.title}
                    className="rounded-xl border bg-card p-6"
                  >
                    <value.icon className="h-8 w-8 text-primary" />
                    <h3 className="mt-4 font-semibold">{value.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                Our Story
              </span>
              <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                From Frustration to Innovation
              </h2>
              <div className="mt-8 space-y-6 text-lg text-muted-foreground">
                <p>
                  ForgeNetwork started with a simple observation: engineering teams
                  spend more time understanding existing code than writing new code.
                  Documentation is always out of date. Knowledge lives in people's
                  heads. Onboarding takes months.
                </p>
                <p>
                  We asked ourselves: what if AI could understand code the way
                  experienced engineers do? What if it could extract features, identify
                  patterns, and generate documentation automatically?
                </p>
                <p>
                  That question led to the Forge Pipeline—our proprietary AI system
                  that analyzes codebases and transforms them into actionable
                  intelligence. Today, ForgeNetwork helps engineering teams around the
                  world ship better software, faster.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                What We Stand For
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  icon: Shield,
                  title: 'Security First',
                  description:
                    'Your code is your most valuable asset. We treat it with the security it deserves. Your code never leaves your control.',
                },
                {
                  icon: Code2,
                  title: 'Developer Experience',
                  description:
                    "We're developers building for developers. Every feature is designed to fit seamlessly into your workflow.",
                },
                {
                  icon: Users,
                  title: 'Transparency',
                  description:
                    'No hidden fees, no surprise limits. We believe in honest pricing and clear communication.',
                },
              ].map((value) => (
                <div key={value.title} className="rounded-xl border bg-card p-8">
                  <value.icon className="h-10 w-10 text-primary" />
                  <h3 className="mt-4 text-xl font-semibold">{value.title}</h3>
                  <p className="mt-3 text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Join Us on This Journey
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Be part of the movement to transform how engineering teams work.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    Get Started Free <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact" search={{ type: undefined }}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-primary-foreground hover:bg-white/10"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
